import React, { useState, useEffect } from 'react';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';

const PricingForm = ({ data, onChange }) => {
  const [errors, setErrors] = useState({});

  const validateField = (field, value) => {
    let error = '';

    switch (field) {
      case 'dailyRate':
        if (!value) {
          error = 'Daily rate is required';
        } else if (isNaN(parseFloat(value)) || parseFloat(value) <= 0) {
          error = 'Daily rate must be a positive number';
        } else if (parseFloat(value) < 10) {
          error = 'Daily rate seems too low (minimum $10)';
        } else if (parseFloat(value) > 10000) {
          error = 'Daily rate seems unusually high (max $10,000)';
        }
        break;
      case 'weeklyRate':
        if (value && (isNaN(parseFloat(value)) || parseFloat(value) <= 0)) {
          error = 'Weekly rate must be a positive number';
        } else if (value && data?.dailyRate && parseFloat(value) >= parseFloat(data?.dailyRate) * 7) {
          error = 'Weekly rate should be less than 7x daily rate to offer a discount';
        }
        break;
      case 'monthlyRate':
        if (value && (isNaN(parseFloat(value)) || parseFloat(value) <= 0)) {
          error = 'Monthly rate must be a positive number';
        } else if (value && data?.dailyRate && parseFloat(value) >= parseFloat(data?.dailyRate) * 30) {
          error = 'Monthly rate should be less than 30x daily rate to offer a discount';
        }
        break;
      case 'deliveryFee':
        if (value && (isNaN(parseFloat(value)) || parseFloat(value) < 0)) {
          error = 'Delivery fee must be a positive number or zero';
        } else if (value && parseFloat(value) > 500) {
          error = 'Delivery fee seems unusually high (max $500)';
        }
        break;
      case 'cleaningFee':
        if (value && (isNaN(parseFloat(value)) || parseFloat(value) < 0)) {
          error = 'Cleaning fee must be a positive number or zero';
        } else if (value && parseFloat(value) > 200) {
          error = 'Cleaning fee seems unusually high (max $200)';
        }
        break;
      case 'securityDeposit':
        if (value && (isNaN(parseFloat(value)) || parseFloat(value) < 0)) {
          error = 'Security deposit must be a positive number or zero';
        } else if (value && parseFloat(value) > 5000) {
          error = 'Security deposit seems unusually high (max $5,000)';
        }
        break;
      default:
        break;
    }

    return error;
  };

  const handleInputChange = (field, value) => {
    onChange({ ...data, [field]: value });
    
    // Clear error for this field when user starts typing
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }

    // Real-time validation for immediate feedback
    let error = validateField(field, value);
    if (error) {
      setErrors(prev => ({ ...prev, [field]: error }));
    }
  };

  const calculateWeeklySuggestion = () => {
    if (!data?.dailyRate) return '';
    const daily = parseFloat(data?.dailyRate);
    return (daily * 6)?.toFixed(2);
  };

  const calculateMonthlySuggestion = () => {
    if (!data?.dailyRate) return '';
    const daily = parseFloat(data?.dailyRate);
    return (daily * 25)?.toFixed(2);
  };

  // Validate required fields on mount
  useEffect(() => {
    let error = validateField('dailyRate', data?.dailyRate);
    if (error) {
      setErrors(prev => ({ ...prev, dailyRate: error }));
    }
  }, []);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-heading text-2xl font-semibold text-foreground mb-2">
          Pricing
        </h2>
        <p className="font-body text-sm text-muted-foreground">
          Set competitive rates to attract renters
        </p>
      </div>

      <div className="space-y-4">
        <div className="p-4 bg-primary/5 border border-primary/20 rounded-md">
          <h3 className="font-heading text-base font-semibold text-foreground mb-3 flex items-center space-x-2">
            <Icon name="DollarSign" size={18} color="var(--color-primary)" strokeWidth={2} />
            <span>Rental Rates</span>
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Input
              label="Daily Rate"
              type="number"
              placeholder="50.00"
              value={data?.dailyRate}
              onChange={(e) => handleInputChange('dailyRate', e?.target?.value)}
              required
              description="Per day rental rate"
              error={errors?.dailyRate}
            />
            <div>
              <Input
                label="Weekly Rate"
                type="number"
                placeholder="280.00"
                value={data?.weeklyRate}
                onChange={(e) => handleInputChange('weeklyRate', e?.target?.value)}
                description="7-day rental rate"
                error={errors?.weeklyRate}
              />
              {data?.dailyRate && !data?.weeklyRate && (
                <p className="mt-1 font-caption text-xs text-primary">
                  Suggested: ${calculateWeeklySuggestion()}
                </p>
              )}
            </div>
            <div>
              <Input
                label="Monthly Rate"
                type="number"
                placeholder="1,200.00"
                value={data?.monthlyRate}
                onChange={(e) => handleInputChange('monthlyRate', e?.target?.value)}
                description="30-day rental rate"
                error={errors?.monthlyRate}
              />
              {data?.dailyRate && !data?.monthlyRate && (
                <p className="mt-1 font-caption text-xs text-primary">
                  Suggested: ${calculateMonthlySuggestion()}
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="p-4 bg-muted rounded-md">
          <h3 className="font-heading text-base font-semibold text-foreground mb-3 flex items-center space-x-2">
            <Icon name="Plus" size={18} strokeWidth={2} />
            <span>Additional Fees (Optional)</span>
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Input
              label="Delivery Fee"
              type="number"
              placeholder="25.00"
              value={data?.deliveryFee}
              onChange={(e) => handleInputChange('deliveryFee', e?.target?.value)}
              description="One-time delivery charge"
              error={errors?.deliveryFee}
            />
            <Input
              label="Cleaning Fee"
              type="number"
              placeholder="15.00"
              value={data?.cleaningFee}
              onChange={(e) => handleInputChange('cleaningFee', e?.target?.value)}
              description="Post-rental cleaning"
              error={errors?.cleaningFee}
            />
            <Input
              label="Security Deposit"
              type="number"
              placeholder="100.00"
              value={data?.securityDeposit}
              onChange={(e) => handleInputChange('securityDeposit', e?.target?.value)}
              description="Refundable deposit"
              error={errors?.securityDeposit}
            />
          </div>
        </div>
      </div>

      {data?.dailyRate && !errors?.dailyRate && (
        <div className="p-4 bg-card border border-border rounded-md">
          <h3 className="font-heading text-base font-semibold text-foreground mb-3">
            Pricing Summary
          </h3>
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="font-body text-sm text-muted-foreground">Daily Rate</span>
              <span className="font-body text-sm font-semibold text-foreground">
                ${parseFloat(data?.dailyRate || 0)?.toFixed(2)}
              </span>
            </div>
            {data?.weeklyRate && !errors?.weeklyRate && (
              <div className="flex justify-between items-center">
                <span className="font-body text-sm text-muted-foreground">Weekly Rate</span>
                <span className="font-body text-sm font-semibold text-foreground">
                  ${parseFloat(data?.weeklyRate)?.toFixed(2)}
                </span>
              </div>
            )}
            {data?.monthlyRate && !errors?.monthlyRate && (
              <div className="flex justify-between items-center">
                <span className="font-body text-sm text-muted-foreground">Monthly Rate</span>
                <span className="font-body text-sm font-semibold text-foreground">
                  ${parseFloat(data?.monthlyRate)?.toFixed(2)}
                </span>
              </div>
            )}
            {(data?.deliveryFee || data?.cleaningFee || data?.securityDeposit) && (
              <div className="pt-2 border-t border-border">
                {data?.deliveryFee && !errors?.deliveryFee && (
                  <div className="flex justify-between items-center">
                    <span className="font-body text-sm text-muted-foreground">Delivery Fee</span>
                    <span className="font-body text-sm text-foreground">
                      ${parseFloat(data?.deliveryFee)?.toFixed(2)}
                    </span>
                  </div>
                )}
                {data?.cleaningFee && !errors?.cleaningFee && (
                  <div className="flex justify-between items-center">
                    <span className="font-body text-sm text-muted-foreground">Cleaning Fee</span>
                    <span className="font-body text-sm text-foreground">
                      ${parseFloat(data?.cleaningFee)?.toFixed(2)}
                    </span>
                  </div>
                )}
                {data?.securityDeposit && !errors?.securityDeposit && (
                  <div className="flex justify-between items-center">
                    <span className="font-body text-sm text-muted-foreground">Security Deposit</span>
                    <span className="font-body text-sm text-foreground">
                      ${parseFloat(data?.securityDeposit)?.toFixed(2)} (refundable)
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      <div className="p-4 bg-muted rounded-md flex items-start space-x-3">
        <Icon name="Info" size={20} color="var(--color-primary)" strokeWidth={2} />
        <div className="flex-1">
          <p className="font-body text-sm text-foreground">
            <span className="font-semibold">Pricing Tips:</span> Research similar trailers in your area. Weekly and monthly discounts encourage longer rentals. Security deposits protect against damage.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PricingForm;